import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseHttpService } from './base-http.service';
import { 
  User,
  CreateUserRequest,
  UpdateUserRequest,
  AuthResponse 
} from '../interfaces/auth.interface';
import { API_ENDPOINTS } from '../constants/api.constants';

/**
 * Service for user management operations
 * Handles CRUD operations for users
 */
@Injectable({
  providedIn: 'root'
})
export class UserManagementService extends BaseHttpService {

  /**
   * Gets all users in the system
   */
  getAllUsers(): Observable<User[]> {
    if (!this.hasValidToken()) {
      return this.returnEmptyArrayWhenNoToken<User>();
    }

    return this.get<any[]>(API_ENDPOINTS.AUTH.USERS).pipe(
      map((backendUsers: any[]) => backendUsers.map(backendUser => this.mapBackendUserToUser(backendUser)))
    );
  }

  /**
   * Maps backend user format to frontend User interface
   */
  private mapBackendUserToUser(backendUser: any): User {
    return {
      id: backendUser.id,
      username: backendUser.username,
      name: backendUser.fullName,
      jobs: backendUser.roleKeys || [],
      jobNames: backendUser.jobTitles || [],
      email: backendUser.email,
      phone: backendUser.phoneNumber,
      created_at: backendUser.createdAt,
      is_active: backendUser.is_active
    };
  }

  /**
   * Gets all call center users
   */
  getCallCenterUsers(): Observable<User[]> {
    if (!this.hasValidToken()) {
      return this.returnEmptyArrayWhenNoToken<User>();
    }

    return this.get<User[]>(API_ENDPOINTS.AUTH.CALL_CENTER_USERS);
  }

  /**
   * Creates a new user
   */
  createUser(userData: CreateUserRequest): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    return this.post<AuthResponse>(API_ENDPOINTS.AUTH.USERS, userData);
  }

  /**
   * Updates an existing user
   */
  updateUser(userId: number, userData: UpdateUserRequest): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    const endpoint = `${API_ENDPOINTS.AUTH.USERS}/${userId}`;
    return this.put<AuthResponse>(endpoint, userData);
  }

  /**
   * Visszaállítja a felhasználó jelszavát a felhasználónevére
   */
  resetPassword(userId: number): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    const endpoint = `${API_ENDPOINTS.AUTH.USERS}/${userId}/reset-password`;
    return this.post<AuthResponse>(endpoint, {});
  }

  /**
   * Deletes a user
   */
  deleteUser(userId: number): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    const endpoint = `${API_ENDPOINTS.AUTH.USERS}/${userId}`;
    return this.delete<AuthResponse>(endpoint);
  }
}